import html2canvas from "html2canvas";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from "@/components";
import type { StatisticsReportModel } from "../../_utils/statisticsReportModel.util";
import { StatisticsReportTemplate } from "../statistics-report-template";
import styles from "./StatisticsReportDrawer.module.scss";

function buildReportFilename(fromIso: string, toIso: string): string {
  const safe = (s: string) => s.replace(/[:]/g, "-");
  return `vetty-statistics-${safe(fromIso)}_${safe(toIso)}.png`;
}

type StatisticsReportDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  model: StatisticsReportModel | null;
};

export const StatisticsReportDrawer = ({
  isOpen,
  onClose,
  model,
}: StatisticsReportDrawerProps) => {
  const exportRef = useRef<HTMLDivElement>(null);
  const previewSizerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useLayoutEffect(() => {
    const el = previewSizerRef.current;
    if (!el) return;

    const update = () => {
      const w = el.clientWidth;
      if (w > 0) {
        setScale(Math.min(1, w / 1024));
      }
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen, model]);

  const handleDownload = useCallback(async () => {
    const node = exportRef.current;
    if (!node || !model) return;

    setDownloadError(null);
    setDownloading(true);

    const templateRoot = node.querySelector<HTMLElement>(
      "[data-statistics-report-template]",
    );
    const prevInline = new Map<string, string>();
    if (templateRoot) {
      const cs = getComputedStyle(templateRoot);
      for (const prop of [
        "background-color",
        "color",
        "border-radius",
      ] as const) {
        prevInline.set(prop, templateRoot.style.getPropertyValue(prop));
        const v = cs.getPropertyValue(prop);
        if (v) templateRoot.style.setProperty(prop, v);
      }
    }

    try {
      if (typeof document !== "undefined" && document.fonts?.ready) {
        await document.fonts.ready;
      }

      const canvas = await html2canvas(node, {
        scale: 1,
        backgroundColor: null,
        useCORS: true,
        logging: false,
        foreignObjectRendering: false,
        onclone: (_clonedDoc, cloned) => {
          cloned.style.opacity = "1";
          cloned.style.visibility = "visible";
        },
      });

      const dataUrl = canvas.toDataURL("image/png");

      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = buildReportFilename(model.fromIso, model.toIso);
      link.click();
    } catch (e) {
      console.error("statistics report PNG", e);
      setDownloadError(
        e instanceof Error
          ? `Could not generate the image: ${e.message}`
          : "Could not generate the image. Try again.",
      );
    } finally {
      if (templateRoot) {
        prevInline.forEach((v, prop) => {
          if (v) templateRoot.style.setProperty(prop, v);
          else templateRoot.style.removeProperty(prop);
        });
      }
      setDownloading(false);
    }
  }, [model]);

  const previewHeight = 1024 * scale;

  return (
    <>
      {model ? (
        <div ref={exportRef} className={styles["exportRoot"]} aria-hidden>
          <StatisticsReportTemplate model={model} />
        </div>
      ) : null}

      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        ariaLabel="Statistics report preview"
      >
        <DrawerHeader title="Report preview" onClose={onClose} />

        <DrawerBody>
          {model ? (
            <div className={styles["preview"]}>
              <div
                ref={previewSizerRef}
                className={styles["preview__sizer"]}
                style={{ height: previewHeight }}
              >
                <div
                  className={styles["preview__scaled"]}
                  style={{ transform: `scale(${scale})` }}
                >
                  <StatisticsReportTemplate model={model} />
                </div>
              </div>
            </div>
          ) : (
            <p>No data to preview.</p>
          )}
          {downloadError ? (
            <p className={styles["error"]} role="alert">
              {downloadError}
            </p>
          ) : null}
        </DrawerBody>

        <DrawerFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleDownload}
            disabled={!model || downloading}
            loading={downloading}
          >
            Download PNG
          </Button>
        </DrawerFooter>
      </Drawer>
    </>
  );
};
